// src/components/WinnerPage.js
import React, {useState, useEffect, useRef} from 'react';
import {electionApi} from '../api/election';
import Confetti from 'react-confetti';
import {motion} from 'framer-motion';

const candName = (c) => `${c?.first_name ?? c?.member?.first_name ?? ''} ${c?.last_name ?? c?.member?.last_name ?? ''}`.trim();
const candPhoto = (c) => c?.photo_data || c?.photo_url || c?.member?.photo_data || null;

const WinnerPage = () => {
    const [winner, setWinner] = useState(null);
    const [finalists, setFinalists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [phase, setPhase] = useState('countdown'); // countdown | selector | reveal
    const [count, setCount] = useState(10);
    const [selIndex, setSelIndex] = useState(0);
    const [showGift, setShowGift] = useState(false);
    const startedRef = useRef(false);

    // Собираем финалистов из результатов и гарантируем попадание победителя
    const setResultsData = (data) => {
        const list = (Array.isArray(data) ? data : []).map(r => r.candidate).filter(Boolean);
        setFinalists(list);
    };

    useEffect(() => {
        electionApi.getWinner()
            .then(res => setWinner(res.data))
            .catch(err => console.error(err));
        electionApi.getPublishedResults()
            .then(res => setResultsData(res.data))
            .catch(() => {
                electionApi.getRoundResults(2).then(r => setResultsData(r.data)).catch(() => {});
            })
            .finally(() => setLoading(false));
    }, []);

    // Запускаем последовательность, когда данные загружены
    useEffect(() => {
        if (loading || !winner || startedRef.current) return;
        startedRef.current = true;
        runSequence();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, winner]);

    useEffect(() => {
        if (phase === 'countdown') return;
        if (phase === 'selector') startSelector();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    // Обратный отсчёт: 10..5 быстро, 4..-2 медленно и крупнее
    const runSequence = () => {
        let value = 10;
        setCount(value);
        const tick = () => {
            setCount(value);
            if (value > 4) {
                value -= 1;
                setTimeout(tick, 1000);
            } else if (value > -2) {
                value -= 1;
                setTimeout(tick, 2000);
            } else {
                // -2 отображаем 2 секунды, затем запускаем бегунок
                setTimeout(() => setPhase('selector'), 2000);
            }
        };
        setTimeout(tick, 0);
    };

    // Бегунок между карточками финалистов, который замедляется и останавливается на победителе
    const startSelector = () => {
        const len = finalists.length;
        if (len <= 1) {
            setPhase('reveal');
            return;
        }
        const targetIndex = finalists.findIndex(f => (f?.id ?? f?.candidate?.id) === (winner.id ?? winner?.candidate?.id));
        const target = targetIndex === -1 ? 0 : targetIndex;
        const fast = 30;
        const slow = 8;
        const total = fast + slow;
        const startIdx = (((target - total) % len) + len) % len;
        let idx = startIdx;
        let step = 0;
        const move = () => {
            idx = (idx + 1) % len;
            setSelIndex(idx);
            step += 1;
            if (step < total) {
                let delay = 90;
                if (step > fast) delay = 150 + (step - fast) * 60;
                setTimeout(move, delay);
            } else {
                setTimeout(() => setPhase('reveal'), 900);
            }
        };
        setTimeout(move, 400);
    };

    const handleGift = () => {
        setShowGift(true);
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center">Загрузка...</div>;
    if (!winner) return <div className="min-h-screen flex items-center justify-center">Победитель не найден</div>;

    const list = finalists.length ? finalists : (winner ? [winner] : []);

    return (
        <div className="min-h-screen bg-gradient-to-b from-gold-accent/20 to-white py-12 px-4 relative overflow-hidden">
            {/* Фейерверки — постоянно */}
            <Confetti/>

            <div className="max-w-6xl mx-auto text-center relative z-10">

                <h1 className="text-5xl font-display mb-8">
                    Новый следующий супружник🚬
                </h1>

                {/* Фаза 1: обратный отсчёт */}
                {phase === 'countdown' && (
                    <div className="py-20">
                        <motion.div
                            key={count}
                            className="font-bold text-gold-accent leading-none"
                            style={{
                                fontSize: `${count <= 4 ? 12 + (20 - count) * 1.2 : 10}rem`,
                                lineHeight: 1
                            }}
                            initial={{scale: 1.2, opacity: 0}}
                            animate={{scale: 1, opacity: 1}}
                            transition={{duration: 0.4}}
                        >
                            {count}
                        </motion.div>
                        <p className="text-2xl mt-6 text-lg">
                            {count <= -1 ? 'Победитель будет объявлен...' : 'Подсчёт голосов...'}
                        </p>
                    </div>
                )}

                {/* Фаза 2: бегунок по карточкам финалистов */}
                {phase === 'selector' && (
                    <div className="min-h-[420px]">
                        <p className="text-2xl text-gray-500 mb-8">А победителем становится...</p>
                        <div className="flex flex-wrap items-center justify-center gap-4">
                            {list.map((c, idx) => {
                                const active = idx === selIndex;
                                const cid = c?.id ?? c?.candidate?.id;
                                return (
                                    <motion.div
                                        key={cid || idx}
                                        className={`rounded-xl overflow-hidden bg-white shadow-lg border-4 transition-all
                                            ${active ? 'border-gold-accent scale-110 shadow-2xl' : 'border-transparent scale-90'}`}
                                        style={{width: '150px'}}
                                        animate={{scale: active ? 1.15 : 0.9}}
                                    >
                                        <div className="aspect-[3/4] bg-gray-100">
                                            {candPhoto(c) ? (
                                                <img src={candPhoto(c)} alt={candName(c)} className="w-full h-full object-cover"/>
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-gray-400">Нет фото</div>
                                            )}
                                        </div>
                                        <div className="p-2 text-sm font-semibold">{candName(c)}</div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Фаза 3: карточка победителя */}
                {phase === 'reveal' && (
                    <>
                        <motion.div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-auto"
                                    initial={{scale: 0.5, opacity: 0}} animate={{scale: 1, opacity: 1}} transition={{type: 'spring', stiffness: 200, damping: 15}}>
                            <div className="h-72 w-72 mx-auto rounded-2xl overflow-hidden border-4 border-gold-accent">
                                {candPhoto(winner) ? (
                                    <img src={candPhoto(winner)} alt={candName(winner)} className="w-full h-full object-cover"/>
                                ) : (
                                    <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">Нет фото</div>
                                )}
                            </div>
                            <h2 className="text-3xl font-bold mt-4">{candName(winner)}</h2>
                            {winner.description && <p className="text-gray-600 mt-2">{winner.description}</p>}
                        </motion.div>

                        <motion.button
                            onClick={handleGift}
                            className="mt-8 px-8 py-3 bg-gold-accent text-white rounded-full text-xl hover:bg-yellow-600 transition"
                            initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} transition={{delay: 0.6}}>
                            🎁 Забрать подарок
                        </motion.button>
                    </>
                )}
            </div>

            {/* Модалка: подарок победителя */}
            {showGift && (
                <GiftModal
                    winner={winner}
                    onClose={() => setShowGift(false)}
                />
            )}
        </div>
    );
};

const GiftModal = ({winner, onClose}) => (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full p-8 relative max-h-[90vh] overflow-y-auto">
            <button onClick={onClose}
                    className="absolute top-2 right-4 text-4xl text-gray-500 hover:text-gray-700">×</button>
            <h2 className="text-3xl font-display mb-6 text-center">🎁 Ваш подарок</h2>

            {/* Жених — слева, супружник — по центру, невеста — справа */}
            <div className="flex items-center justify-center gap-4 sm:gap-6 mb-6">
                <div className="flex-1 max-w-[200px] aspect-[3/4] bg-gray-100 rounded-xl overflow-hidden shadow-lg">
                    <img src="/images/hero-left.jpg" alt="Невеста Татьяна" className="w-full h-full object-cover"/>
                    <div className="text-center text-sm font-semibold py-1 bg-gray-50">Татьяна</div>
                </div>
                <div className="flex-1 max-w-[240px] aspect-[3/4] bg-gray-100 rounded-xl overflow-hidden shadow-lg border-4 border-gold-accent">
                    {candPhoto(winner) ? (
                        <img src={candPhoto(winner)} alt={candName(winner)} className="w-full h-full object-cover"/>
                    ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">Нет фото</div>
                    )}
                    <div className="text-center text-sm font-semibold py-1 bg-gray-50">{candName(winner)}</div>
                </div>
                <div className="flex-1 max-w-[200px] aspect-[3/4] bg-gray-100 rounded-xl overflow-hidden shadow-lg">
                    <img src="/images/hero-right.jpg" alt="Жених Илья" className="w-full h-full object-cover"/>
                    <div className="text-center text-sm font-semibold py-1 bg-gray-50">Илья</div>
                </div>
            </div>

            <div className="text-center">
                <p className="text-xl font-display mb-3">К Вам на свадьбу приедут Татьяна и Илья</p>
                <p className="text-gray-600">
                    Дорогой(ая) {candName(winner)}, мы рады подарить вам наш визит на вашу свадьбу!
                </p>
                <p className="text-gray-400 text-sm mt-2">Место и время ждём в тг.</p>
                <button onClick={onClose}
                        className="mt-5 px-8 py-2 bg-gold-accent text-white rounded hover:bg-yellow-600">
                    Понятно
                </button>
            </div>
        </div>
    </div>
);

export default WinnerPage;